import { useEffect, useState } from "react";

import { parseResults } from "./results";
import type { ExternalSource, ResultsSearch } from "./types";

/* Nov 3 counts and race calls from civicAPI's race search: free, no key,
   open to cross-origin requests, credit required. Federal races are filed
   under "General", governors under "Statewide". Its edge cache keeps a
   response 15 seconds; the tracker asks once a minute. */
const ELECTION_TYPES = ["General", "Statewide"];
const POLL_MS = 60_000;

export const RESULTS_SOURCE_ID = "builtin-live-results";

const resultsUrl = (electionType: string) =>
  `https://civicapi.org/api/v2/race/search?country=US&election_type=${electionType}&startDate=2026-11-03&endDate=2026-11-03&limit=5000`;

const load = async (signal: AbortSignal) => {
  const pages = await Promise.all(
    ELECTION_TYPES.map(async (electionType) => {
      const response = await fetch(resultsUrl(electionType), { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return ((await response.json()) as ResultsSearch).races;
    })
  );
  return parseResults(pages.flat());
};

/** Actual results as a built-in source, refreshed every minute while the
    tracker is open; a failed refresh keeps the last good read. */
const useLiveResults = () => {
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<ExternalSource | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const rows = await load(controller.signal);
        setSource({
          builtIn: true,
          credit: "live from civicAPI (civicapi.org)",
          id: RESULTS_SOURCE_ID,
          importedAt: new Date().toISOString(),
          label: "Results",
          results: true,
          rows,
        });
        setError(null);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      }
      if (!controller.signal.aborted) {
        timer = setTimeout(() => void poll(), POLL_MS);
      }
    };

    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, []);

  return { error, source };
};

export default useLiveResults;
